import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import type { Feature, GeometryObject, LineString } from 'geojson';

/**
 * Geometry for the field order overlay, shared by mapmaking and sampling.
 *
 * The shift's jobs are sampled one after another, so what both maps show is the
 * number each field wears and the hop from each field to the next. Hops run
 * number to number — not between pullins, which is what the (separate) pullin
 * order line already shows. The field being worked on wears no number, so its
 * hops meet its border instead.
 */

/** Marks the hop features this module produces, for the map layer to pick out. */
export const FIELD_ORDER_FEATURE = 'field-order';

export type Ring = number[][];

export interface FieldShape {
  /** Outer rings and holes of one field, in projected map units. */
  rings: Ring[];
}

export interface BorderHop {
  /** Where the hop leaves the earlier field. */
  from: [number, number];
  /** Where the hop meets the later field. */
  to: [number, number];
}

export interface HopArrow {
  coordinate: [number, number];
  /** OpenLayers text rotation, radians, clockwise-positive. */
  rotation: number;
}

/**
 * Outlines a boundary geometry as a flat list of rings. A job with no boundary
 * attachment is represented by its pullin, which outlines to a single vertex.
 */
export function ringsFromGeometry(geometry: GeometryObject): Ring[] {
  switch (geometry.type) {
    case 'Polygon':
      return geometry.coordinates as Ring[];
    case 'MultiPolygon':
      return (geometry.coordinates as Ring[][]).flat();
    case 'Point':
      return [[geometry.coordinates as number[]]];
    default:
      return [];
  }
}

/**
 * Consecutive vertex pairs of a ring. A lone vertex becomes a zero-length
 * segment so that pullin-only jobs still take part in the search.
 */
function segmentsOf(ring: Ring): [number[], number[]][] {
  if (ring.length === 1) {
    return [[ring[0], ring[0]]];
  }

  const segments: [number[], number[]][] = [];

  for (let i = 0; i < ring.length - 1; i++) {
    segments.push([ring[i], ring[i + 1]]);
  }

  return segments;
}

function closestPointOnSegment(point: number[], start: number[], end: number[]): [number, number] {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return [start[0], start[1]];
  }

  const t = Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lengthSquared));

  return [start[0] + t * dx, start[1] + t * dy];
}

/**
 * The two closest points of two field borders.
 *
 * Both directions are searched — every vertex of one shape against every
 * segment of the other — because the closest point usually lies partway along
 * an edge rather than on a vertex, and only one of the two shapes has a vertex
 * there. Returns null when either shape has no coordinates to connect.
 */
export function closestBorderPoints(a: FieldShape, b: FieldShape): BorderHop | null {
  let best: BorderHop | null = null;
  let bestDistance = Infinity;

  const consider = (from: [number, number], to: [number, number]) => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const distance = dx * dx + dy * dy;

    if (distance < bestDistance) {
      bestDistance = distance;
      best = { from, to };
    }
  };

  const aSegments = a.rings.flatMap(segmentsOf);
  const bSegments = b.rings.flatMap(segmentsOf);

  for (const ring of a.rings) {
    for (const vertex of ring) {
      for (const [start, end] of bSegments) {
        consider([vertex[0], vertex[1]], closestPointOnSegment(vertex, start, end));
      }
    }
  }

  for (const ring of b.rings) {
    for (const vertex of ring) {
      for (const [start, end] of aSegments) {
        consider(closestPointOnSegment(vertex, start, end), [vertex[0], vertex[1]]);
      }
    }
  }

  return best;
}

/**
 * Direction marker for one hop, centred on it and turned to follow it.
 * Returns null for a hop with no length, which has no direction to show.
 */
export function segmentArrow(from: number[], to: number[]): HopArrow | null {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];

  if (dx === 0 && dy === 0) {
    return null;
  }

  return {
    coordinate: [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2],
    // The "➤" glyph renders pointing east (right). OpenLayers text rotation is
    // clockwise-positive; screen y grows downward while the projected y-axis
    // grows upward. To align with the projected hop direction, rotate by
    // -atan2(dy, dx).
    rotation: -Math.atan2(dy, dx),
  };
}

/**
 * Where a field carries its name, and so where its order number belongs.
 *
 * Uses OpenLayers' own interior point — the anchor it places polygon labels on —
 * so the number lines up under the name instead of drifting to a centroid that
 * may not even fall inside a concave field. A split field labels its widest
 * part, which OpenLayers reports as the interior point with the largest M.
 *
 * Returns null for geometry with no area, which has no inside to label.
 */
export function fieldLabelPoint(geometry: GeometryObject): [number, number] | null {
  if (geometry.type === 'Polygon') {
    const [x, y] = new Polygon(geometry.coordinates as number[][][]).getInteriorPoint().getCoordinates();

    return [x, y];
  }

  if (geometry.type === 'MultiPolygon') {
    const interiorPoints = new MultiPolygon(geometry.coordinates as number[][][][])
      .getInteriorPoints()
      .getCoordinates();

    if (interiorPoints.length === 0) {
      return null;
    }

    // M is the width of the horizontal slice the point sits on, so the largest
    // M marks the part with the most room for a label.
    const widest = interiorPoints.reduce((best, point) => (point[2] > best[2] ? point : best));

    return [widest[0], widest[1]];
  }

  return null;
}

/** The zone label a boundary attachment gives its field polygons. */
const FIELD_ZONE_NAME = 'field';

/**
 * Picks the field out of a boundary attachment.
 *
 * An attachment holds the field alongside its slow and unsafe zones, and those
 * auxiliary zones are frequently identical across the jobs of one shift — so
 * measuring between whole attachments finds two copies of the same slow zone,
 * zero distance apart, instead of the two field borders.
 *
 * Parts that say nothing about which zone they are are all kept, since there is
 * then no field to single out.
 */
export function fieldGeometries<T extends { properties?: { name?: string } | null; geometry: GeometryObject }>(
  parts: T[],
): GeometryObject[] {
  if (parts.every((part) => !part.properties?.name)) {
    return parts.map((part) => part.geometry);
  }

  return parts.filter((part) => part.properties?.name === FIELD_ZONE_NAME).map((part) => part.geometry);
}

/**
 * Outlines one job's field from whatever parts are on hand — the features of a
 * parsed boundary attachment, or the live zones of the mission being map-made.
 * A job with no boundary yet is represented by its pullin, so it still takes
 * its place in the shift's order.
 *
 * @param parts Boundary geometries belonging to a single job.
 * @param pullin Projected pullin coordinate to fall back on.
 */
export function shapeFromParts(parts: GeometryObject[], pullin?: number[]): FieldShape {
  const rings = parts.flatMap(ringsFromGeometry).filter((ring) => ring.length > 0);

  if (rings.length === 0 && pullin) {
    return { rings: [[pullin]] };
  }

  return { rings };
}

export interface OrderedField {
  jobId: string;
  shape: FieldShape;
  /** Where the field wears its number; null when it has no inside to label. */
  labelPoint: [number, number] | null;
  /** Already sampled: holds its place in the shift, but is no longer routed to. */
  done?: boolean;
}

/**
 * The number each job of the shift wears, counting from 1 in working order.
 * Jobs with nothing to draw still hold their place, so the rest keep the
 * numbers the shift gave them. Finished work wears no number of its own.
 */
export function shiftOrderByJobId(fields: OrderedField[]): Map<string, number> {
  return new Map(fields.flatMap((field, idx) => (field.done ? [] : [[field.jobId, idx + 1] as [string, number]])));
}

/** Which shift a job belongs to, and where it sits in that shift's working order. */
export interface ShiftPlacement {
  shiftId: string | undefined;
  order: number | undefined;
}

/**
 * Splits a schedule into the runs the order overlay draws: one run per shift,
 * each in the order ops gave it.
 *
 * With a shift in hand — a job is open, so we know which one is being worked —
 * only that shift is drawn. Without one the operator has not started yet, and
 * every shift on the schedule is drawn instead, so the day can be read before
 * the first field is opened. Shifts stay separate runs either way: order runs
 * from 0 within a shift, so two shifts share neither numbers nor a route.
 *
 * @param jobs The schedule, in any order.
 * @param placementOf Reads a job's shift and its place in that shift.
 * @param currentShiftId The shift being worked, if one is known.
 */
export function shiftRuns<T>(jobs: T[], placementOf: (job: T) => ShiftPlacement, currentShiftId?: string): T[][] {
  const runs = new Map<string, T[]>();

  for (const job of jobs) {
    const { shiftId } = placementOf(job);

    // A job on no shift has no working order to place it in.
    if (!shiftId || (currentShiftId && shiftId !== currentShiftId)) {
      continue;
    }

    runs.set(shiftId, [...(runs.get(shiftId) ?? []), job]);
  }

  return [...runs.values()].map((run) =>
    [...run].sort((a, b) => (placementOf(a).order ?? Infinity) - (placementOf(b).order ?? Infinity)),
  );
}

/** The closest point of a field's border to somewhere else on the map. */
function closestBorderPointTo(shape: FieldShape, point: [number, number]): [number, number] | null {
  return closestBorderPoints(shape, { rings: [[point]] })?.from ?? null;
}

/**
 * Where one hop starts and ends.
 *
 * Hops run number to number, since the numbers are what the operator reads. The
 * field being worked on is the exception: it wears no number, so the hop meets
 * its border instead — arriving at the border on the way in, leaving it on the
 * way out.
 */
function hopBetween(from: OrderedField, to: OrderedField, currentJobId?: string): BorderHop | null {
  if (to.jobId === currentJobId) {
    const border = from.labelPoint && closestBorderPointTo(to.shape, from.labelPoint);

    return border ? { from: from.labelPoint!, to: border } : null;
  }

  if (from.jobId === currentJobId) {
    const border = to.labelPoint && closestBorderPointTo(from.shape, to.labelPoint);

    return border ? { from: border, to: to.labelPoint! } : null;
  }

  return from.labelPoint && to.labelPoint ? { from: from.labelPoint, to: to.labelPoint } : null;
}

/**
 * The hops that make up a shift's field order, one GeoJSON LineString each.
 *
 * Fields with nothing to draw, and fields already sampled, are hopped over
 * rather than renumbered, keeping the numbers true to the shift.
 *
 * @param fields Every field of the shift, in the order they are sampled.
 * @param currentJobId The job open right now, which wears no number.
 */
export function fieldOrderFeatures(fields: OrderedField[], currentJobId?: string): Feature<LineString>[] {
  const drawable = fields
    .map((field, idx) => ({ ...field, order: idx + 1 }))
    .filter((field) => !field.done && field.shape.rings.some((ring) => ring.length > 0));

  const hops: Feature<LineString>[] = [];

  for (let i = 0; i < drawable.length - 1; i++) {
    const from = drawable[i];
    const to = drawable[i + 1];
    const hop = hopBetween(from, to, currentJobId);

    if (!hop) {
      continue;
    }

    hops.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [hop.from, hop.to] },
      properties: {
        name: FIELD_ORDER_FEATURE,
        fromOrder: from.order,
        toOrder: to.order,
      },
    });
  }

  return hops;
}

/** A job on the schedule, as the field order overlay sees it. */
export interface ScheduledField extends OrderedField, ShiftPlacement {}

export interface FieldOrderOverlay {
  /** The number each field wears, by job. Finished work wears none. */
  orderByJobId: Map<string, number>;
  /** The connectors between the fields of each shift. */
  hops: Feature<LineString>[];
}

/**
 * The whole field order overlay for a schedule: what each field is numbered,
 * and the route between them.
 *
 * Each shift is numbered and routed on its own, so two shifts never share a
 * number or a connector. Which shifts are drawn depends on the job in hand —
 * see shiftRuns.
 *
 * @param fields Every job on the schedule, in any order.
 * @param openJobId The job open right now, which wears no number of its own.
 * @param shiftJobId The job whose shift to draw; the open one unless it has been
 *   closed, in which case the shift stays put and the field gets its number back.
 */
export function fieldOrderOverlay(
  fields: ScheduledField[],
  openJobId?: string,
  shiftJobId: string | undefined = openJobId,
): FieldOrderOverlay {
  const currentShiftId = fields.find((field) => field.jobId === shiftJobId)?.shiftId;
  const orderByJobId = new Map<string, number>();
  const hops: Feature<LineString>[] = [];

  for (const run of shiftRuns(fields, (field) => field, currentShiftId)) {
    for (const [jobId, order] of shiftOrderByJobId(run)) {
      orderByJobId.set(jobId, order);
    }

    hops.push(...fieldOrderFeatures(run, openJobId));
  }

  return { orderByJobId, hops };
}
